"use strict";

import { CONSTANTS } from "~/helpers/constants";
import { getElement } from "~/utils/dom";

/**
 * Generate a unique key for a script element
 * @param prefix Prefix for the key
 * @param index Index of the script element
 * @returns A unique key string
 */
export function generateScriptKey(prefix: string, index: number): string {
  return `${prefix}_${index}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function isFromSameHost(src?: string): boolean {
  if (!src) return false;

  if (src.startsWith("/") && !src.startsWith("//")) {
    return true;
  }

  if (!src.startsWith("http") && !src.startsWith("//") && !src.match(/^[a-zA-Z]+:/)) {
    return true;
  }

  try {
    const currentHost = window.location.hostname;

    const urlToCheck = src.startsWith("//") ? `${window.location.protocol}${src}` : src;

    const scriptUrl = new URL(urlToCheck, window.location.origin);
    const scriptHost = scriptUrl.hostname;

    return currentHost === scriptHost;
  } catch (e) {
    console.error(`Error in isFromSameHost: ${e}`, src);
    return false;
  }
}

/**
 * Destroy the banner and styles
 */
export function destroyBanner() {
  const element = getElement<HTMLDivElement>(`.${CONSTANTS.BANNER.SELECTOR}`, document, false);
  const styles = getElement<HTMLStyleElement>(`[name="${CONSTANTS.BANNER.SELECTOR}"]`, document, false);

  if (element) element.remove();
  if (styles) styles.remove();
}

/**
 * Check if a script should be considered mandatory
 * @param node The script element to check
 * @returns True if the script should be considered mandatory, false otherwise
 */
export function isMandatoryScript(node?: HTMLScriptElement | (SVGScriptElement & { src?: string })): boolean {
  const { currentScript } = document;

  if (!currentScript && !node) return false;

  const hasCookieconsentAttr = node?.hasAttribute?.(CONSTANTS.DEFAULT.DATASET_NAME);

  if (node?.id === CONSTANTS.DEFAULT.SCRIPT_NAME) {
    return true;
  }

  if (hasCookieconsentAttr) {
    return false;
  }

  if (node?.src && isFromSameHost(node.src)) {
    return true;
  }

  return false;
}

/**
 * Get the cookiedock cuuid from the script tag
 * @param name The name of the dataset attribute
 * @returns The cookiedock cuuid
 */
export function getCookiedockCuuid(name: string): string | void {
  const scriptTag: NodeListOf<HTMLElement> = document.querySelectorAll(`[${CONSTANTS.DEFAULT.SCRIPT_ID}]`);

  if (scriptTag.length > 1) {
    console.warn(CONSTANTS.CONSOLE.TOO_MANY_SCRIPTS);
    return;
  }

  if (!scriptTag[0]?.dataset[name]) {
    console.warn(CONSTANTS.CONSOLE.NO_CUUID_FOUND);
    return;
  }

  return scriptTag[0]?.dataset[name];
}

/**
 * Logs a styled Cookiedock error message to the console.
 * @param message The error message to display
 * @param support Optional support link (defaults to cookiedock.com)
 */
export function logCookiedockError(message: string, support?: string) {
  const supportUrl = support || import.meta?.env?.VITE_SUPPORT_URL || "https://cookiedock.com";
  console.groupCollapsed("%c🍪 Cookiedock Error", "background: #10a2ab; color: white; font-weight: bold; padding: 6px 14px; border-radius: 6px;");
  console.log("%c🛑%c %s", "font-weight: bold;", "color: #10a2ab;", message || new Error().stack);
  console.log("%c📘%c %s", "font-weight: bold;", "color: #10a2ab;", supportUrl);
  console.groupEnd();
}

export function allowOneDetailsOpen(selector: string): void {
  const details = getElement<HTMLDetailsElement>(selector, document, true);

  details.forEach((targetDetail) => {
    targetDetail.addEventListener("click", () => {
      details.forEach((detail) => {
        if (detail !== targetDetail) {
          detail.removeAttribute("open");
        }
      });
    });
  });
}

export function combineCheckboxes() {
  const bannerInputs = getElement<HTMLInputElement>(`.${CONSTANTS.PREFERENCES.BANNER}`, document, true);
  const dialogInputs = getElement<HTMLInputElement>(`.${CONSTANTS.PREFERENCES.DIALOG} input[type="checkbox"]`, document, true);

  if (!bannerInputs || !dialogInputs) return;

  const count = Math.min(bannerInputs.length, dialogInputs.length);

  for (let i = 0; i < count; i++) {
    const bannerInput = bannerInputs[i]!;
    const dialogInput = dialogInputs[i]!;

    bannerInput.checked = dialogInput.checked;

    bannerInput.addEventListener("change", () => {
      if (!dialogInput.disabled) dialogInput.checked = bannerInput.checked;
    });
    dialogInput.addEventListener("change", () => {
      if (!bannerInput.disabled) bannerInput.checked = dialogInput.checked;
    });
  }
}

export function formatExpiry(expiry: string | number): string {
  if (typeof expiry === "string" && isNaN(Number(expiry))) {
    return expiry;
  }

  const expiryNum = Number(expiry);

  if (isNaN(expiryNum)) {
    return "Unknown";
  }

  if (expiryNum > 1000000000) {
    return new Date(expiryNum * 1000).toLocaleString(Intl.DateTimeFormat().resolvedOptions().locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  if (expiryNum > 0) {
    const timeUnits = new Map([
      ["year", Math.floor(expiryNum / 31536000)],
      ["month", Math.floor((expiryNum % 31536000) / 2592000)],
      ["day", Math.floor((expiryNum % 2592000) / 86400)],
      ["hour", Math.floor((expiryNum % 86400) / 3600)],
      ["minute", Math.floor((expiryNum % 3600) / 60)],
    ]);

    const parts: string[] = [];

    for (const [unit, value] of timeUnits) {
      if (value > 0) {
        parts.push(`${value} ${unit}${value !== 1 ? "s" : ""}`);
      }
    }

    return parts.length > 0 ? parts.join(" ") : "0 minutes";
  }

  return "Session";
}

/**
 * Apply template with replacements
 * @param template The HTML template string
 * @param replacements Object containing key-value pairs to replace in the template
 * @param conditionals Object containing conditional blocks to process
 * @returns Processed HTML string
 */
export function applyTemplate(template: string, replacements: Record<string, any>, conditionals: Record<string, boolean> = {}): string {
  let result = template;

  const nestedPropertyRegex = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*)\s*}}/g;
  result = result.replace(nestedPropertyRegex, (match, propertyPath) => {
    const keys = propertyPath.split(".");
    let value = replacements;

    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return match;
      }
    }

    return value?.toString() || "";
  });

  Object.entries(replacements).forEach(([key, value]) => {
    if (typeof value === "string") {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      result = result.replace(regex, value);
    }
  });

  Object.entries(conditionals).forEach(([key, condition]) => {
    result = result.replace(new RegExp(`{{#${key}}}(.*?){{\\/${key}}}`, "gs"), condition ? "$1" : "");
    result = result.replace(new RegExp(`{{\\^${key}}}(.*?){{\\/${key}}}`, "gs"), !condition ? "$1" : "");
  });

  return result.replace(/\n/g, "");
}
